import React, { useState } from 'react';
import { FormControl, InputLabel, MenuItem, Modal, Select, Stack, Typography } from '@mui/material';
import GenericTextField from '../../../components/GenericTextField/GenericTextField';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import MoneyField from '../../../components/MoneyField';
import { CommonModalActionsBox, CommonModalContentBox, CommonModalWrapperBox } from '../../../common/styles/Common.styles';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { ActionMode, GeneralStatusEnum } from '../../../common/enums/common.enum';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { BILLING_ITEM_TYPE_LABELS, BillingItemTypeEnum } from '../../../common/enums/invoicing.enum';
import { BillingItemDto } from '../../../common/interfaces/invoicing.interface';
import { createPlatformBillingItem, updatePlatformBillingItem } from '../../../api/invoicing.api';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../theme';
interface PlatformItemModalProps {
  mode: ActionMode;
  item?: BillingItemDto | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Platform items always carry a VAT rate, unlike the kindergarten screen which hides it
 * outside the VAT system - the rate is a property of the item, and each kindergarten
 * decides whether VAT is shown at all.
 */
const PlatformItemModal: React.FC<PlatformItemModalProps> = ({ mode, item, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(item?.name ?? '');
  const [pdfLabel, setPdfLabel] = useState(item?.pdfLabel ?? '');
  const [type, setType] = useState<BillingItemTypeEnum>(item?.type ?? BillingItemTypeEnum.FIXED_QUANTITY);
  const [unitPriceNet, setUnitPriceNet] = useState<number | null>(item?.unitPriceNet ?? null);
  const [vatRate, setVatRate] = useState<number>(item?.vatRate ?? 0);
  const [unit, setUnit] = useState(item?.unit ?? '');
  const [status, setStatus] = useState<GeneralStatusEnum>(item?.status ?? GeneralStatusEnum.ACTIVE);

  const isAttendance = type === BillingItemTypeEnum.ATTENDANCE_BASED;

  const handleSave = async (): Promise<void> => {
    if (!name.trim() || !pdfLabel.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, t('billing.items.validation.nameAndLabelRequired'));
      return;
    }
    if (unitPriceNet === null) {
      createNotification(MESSAGE_TYPE.ERROR, t('billing.items.validation.priceRequired'));
      return;
    }
    const dto = {
      name: name.trim(),
      pdfLabel: pdfLabel.trim(),
      type,
      unitPriceNet,
      vatRate,
      unit: unit.trim() || undefined,
      defaultQuantity: 1,
      status
    };
    try {
      if (mode === ActionMode.CREATE) {
        await createPlatformBillingItem(dto);
      } else if (item) {
        await updatePlatformBillingItem(item.id, dto);
      }
      createNotification(MESSAGE_TYPE.SUCCESS, t('billing.platform.saved'));
      onSaved();
      onClose();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('billing.items.saveFailed'));
    }
  };

  return (
    <Modal open onClose={onClose}>
      <CommonModalWrapperBox sx={{ width: 620 }}>
        <CommonModalContentBox>
          <Typography variant="h2" sx={{ fontSize: 22, mb: 1 }}>
            {mode === ActionMode.CREATE ? t('billing.platform.create') : t('billing.platform.edit')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 3 }}>{t('billing.platform.followsChange')}</Typography>

          <Stack spacing={2.5}>
            <Stack direction="row" spacing={2}>
              <GenericTextField
                label={t('billing.items.internalName')}
                required
                value={name}
                onChange={e => setName(e.target.value)}
                helperText={t('billing.platform.sameEverywhere')}
              />
              <GenericTextField
                label={t('billing.lines.invoiceLabel')}
                required
                value={pdfLabel}
                onChange={e => setPdfLabel(e.target.value)}
                helperText={t('billing.platform.labelHelp')}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="platform-type-label">{t('billing.items.calculation')}</InputLabel>
                <Select
                  labelId="platform-type-label"
                  label={t('billing.items.calculation')}
                  value={type}
                  onChange={e => setType(e.target.value as BillingItemTypeEnum)}
                  sx={{ borderRadius: '10px', backgroundColor: palette.surface.main }}
                >
                  {Object.values(BillingItemTypeEnum).map(value => (
                    <MenuItem key={value} value={value}>
                      {t(BILLING_ITEM_TYPE_LABELS[value])}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <GenericTextField
                label={t('billing.items.unit')}
                value={unit}
                onChange={e => setUnit(e.target.value)}
                helperText={isAttendance ? t('billing.items.unitExampleAttendance') : t('billing.items.unitExampleFixed')}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <MoneyField label={t('billing.items.netUnitPrice')} required value={unitPriceNet} onChange={setUnitPriceNet} />
              <FormControl fullWidth>
                <InputLabel id="platform-vat-label">{t('billing.items.vatRate')}</InputLabel>
                <Select
                  labelId="platform-vat-label"
                  label={t('billing.items.vatRate')}
                  value={vatRate}
                  onChange={e => setVatRate(Number(e.target.value))}
                  sx={{ borderRadius: '10px', backgroundColor: palette.surface.main }}
                >
                  {[0, 10, 20].map(rate => (
                    <MenuItem key={rate} value={rate}>
                      {rate}%
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="platform-status-label">{t('common.status')}</InputLabel>
                <Select
                  labelId="platform-status-label"
                  label={t('common.status')}
                  value={status}
                  onChange={e => setStatus(e.target.value as GeneralStatusEnum)}
                  sx={{ borderRadius: '10px', backgroundColor: palette.surface.main }}
                >
                  <MenuItem value={GeneralStatusEnum.ACTIVE}>{t('enums.generalStatus.Active')}</MenuItem>
                  <MenuItem value={GeneralStatusEnum.INACTIVE}>{t('enums.generalStatus.Inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </CommonModalContentBox>

        <CommonModalActionsBox>
          <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" onClick={onClose} label={t('common.cancel')} />
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            onAsyncAction={handleSave}
            label={t('common.save')}
          />
        </CommonModalActionsBox>
      </CommonModalWrapperBox>
    </Modal>
  );
};

export default PlatformItemModal;
